import { useMemo } from 'react';

import { getPresetDateRange, toApiDate } from '../model/period';

import type { SearchCondition } from '@kc-console/shared-ui';
import type { PeriodPreset } from '../model/period';
import type { GetConsoleEventsRequest } from '../model/useGetConsoleEvents';

const getSortParam = (searchCondition: SearchCondition): string | undefined => {
  const first = searchCondition.sort[0];
  if (!first) return undefined;
  return `${first.id},${first.direction}`;
};

interface EventDateFilterValue {
  customRange: [Date | null, Date | null];
  periodPreset: PeriodPreset;
}

/**
 * SearchCondition과 날짜 필터 상태를 Console Events API 요청 파라미터로 변환한다.
 * @description
 * - 기간 프리셋(`periodPreset`)이 `all`/`24h`/`7d`/`30d`이면 해당 프리셋의 계산된 [from, to]를 사용한다.
 * - `all`은 최대 조회 기간인 1년 전 ~ 현재 범위를 사용한다.
 * - `custom`이면 직접 선택한 `customRange` 값을 사용한다.
 * - 제목/상태 필터는 테이블 SearchCondition에서 추출하여 `title`/`eventStatus` 파라미터로 전달한다.
 * - `sort`는 사용자가 정렬을 선택한 경우에만 `{id},{direction}` 형태로 직렬화한다.
 */
const useEventsRequestParams = (
  searchCondition: SearchCondition,
  dateFilter: EventDateFilterValue,
): Omit<GetConsoleEventsRequest, 'page' | 'limit'> => {
  const { periodPreset, customRange } = dateFilter;

  return useMemo(() => {
    const sort = getSortParam(searchCondition);

    let range: [Date, Date] | null = null;

    if (periodPreset === 'custom') {
      const [start, end] = customRange;
      if (start && end) range = [start, end];
    } else {
      range = getPresetDateRange(periodPreset);
    }

    const fromDate = range ? toApiDate(range[0]) : undefined;
    const toDate = range ? toApiDate(range[1]) : undefined;

    const eventStatus = searchCondition.filter.find(f => f.id === 'eventStatus')?.values?.[0] as
      | string
      | undefined;

    const titleRaw = searchCondition.filter.find(f => f.id === 'title')?.values?.[0];
    const title =
      typeof titleRaw === 'string' && titleRaw.trim() !== '' ? titleRaw.trim() : undefined;

    return { sort, fromDate, toDate, eventStatus, title };
  }, [searchCondition, periodPreset, customRange]);
};

export default useEventsRequestParams;
